/* eslint-disable */
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useDrop } from "react-dnd";
import { twJoin, twMerge } from "tailwind-merge";

import { SupTable, SupButton, SupPlus } from "@repo/core-ui";

import { SupFormGroup } from "../FormGroup";
import { useTableAdapter } from "./hooks/useTableAdapter";
import type { ArrayFieldProps, FieldValues } from "./types";

export function SupArrayFields<N extends string, R extends FieldValues>({
  name,
  className,
  tableClassName,
  columns,
  control,
  defaultExpanded = true,
  title,
  description,
  shouldUnregister,
  defaultValues,
  defaultActiveCell,
  buttonDisabled = false,
  disabled = false,
  renderControls,
  getRowConfig,
  getCellConfig,
  onCellClick,
  onRowClick,
}: ArrayFieldProps<N, R>) {
  const { trigger, getFieldState, formState } = useFormContext();
  const {
    columns: tColumns,
    rows,
    appendRow,
  } = useTableAdapter<R, N>(name, {
    columns,
    control,
    shouldUnregister,
    defaultValues,
    defaultActiveCell,
    disabled,
    getRowConfig,
    getCellConfig,
    renderControls,
  });
  const { invalid, isTouched } = getFieldState(name, formState);
  const [, drop] = useDrop(() => ({ accept: name }));

  useEffect(() => {
    void trigger(name);
  }, [name]);

  return (
    <SupFormGroup
      invalid={invalid && isTouched}
      title={title}
      description={description}
      count={rows.length}
      collapsible={!invalid}
      defaultExpanded={defaultExpanded}
      className={className}
    >
      <div
        ref={el => {
          drop(el?.getElementsByTagName("tbody")[0] ?? el);
        }}
        className={twJoin("border-0.5", invalid && isTouched ? "border-error-700" : "border-neutral-600")}
      >
        <SupTable
          className={twMerge("table-fixed", tableClassName)}
          rowClassName="border-b-0.5 border-neutral-100 transition-colors duration-200 [&:hover_.sup-array-field-controls]:inline-flex"
          data={rows}
          columns={tColumns}
          rowId="id"
          onRowClick={onRowClick}
          onCellClick={(row, col) => {
            const column = columns.find(c => c.field === col.field);

            if (column) onCellClick?.(row, column);
          }}
        />

        <div className="px-4 py-1.5 bg-neutral-25 border-t-0.5 border-neutral-100">
          <SupButton disabled={invalid || buttonDisabled || disabled} prefixIcon={<SupPlus />} variant="text" onClick={appendRow}>
            New
          </SupButton>
        </div>
      </div>
    </SupFormGroup>
  );
}
